'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

const TOAST_ICONS: Record<ToastType, React.ReactNode> = {
  success: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="7" stroke="#4ade80" strokeWidth="1.4" />
      <path
        d="M5 8l2 2 4-4"
        stroke="#4ade80"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  error: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="7" stroke="#f87171" strokeWidth="1.4" />
      <path d="M8 5v4M8 11v.5" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  info: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="7" stroke="#63b3ed" strokeWidth="1.4" />
      <path d="M8 7v4M8 5v.5" stroke="#63b3ed" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  warning: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 2L14.5 13H1.5L8 2z" stroke="#f6ad55" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 7v3M8 11.5v.5" stroke="#f6ad55" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
};

const TOAST_STYLES: Record<ToastType, { bg: string; border: string; color: string }> = {
  success: { bg: 'rgba(34,197,94,0.1)', border: 'rgba(74,222,128,0.3)', color: '#4ade80' },
  error: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(248,113,113,0.3)', color: '#f87171' },
  info: { bg: 'rgba(99,179,237,0.1)', border: 'rgba(99,179,237,0.3)', color: '#63b3ed' },
  warning: { bg: 'rgba(246,173,85,0.1)', border: 'rgba(246,173,85,0.3)', color: '#f6ad55' },
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const style = TOAST_STYLES[toast.type];

  useEffect(() => {
    // Trigger enter animation
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onDismiss(toast.id), 300);
  }, [toast.id, onDismiss]);

  useEffect(() => {
    const duration = toast.duration ?? 4000;
    if (duration <= 0) return;
    const t = setTimeout(() => handleDismiss(), duration);
    return () => clearTimeout(t);
  }, [toast.duration, handleDismiss]);

  return (
    <div
      style={{
        background: '#0f1623',
        border: `1px solid ${style.border}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        borderRadius: '12px',
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        minWidth: '280px',
        maxWidth: '360px',
        transform: visible && !leaving ? 'translateX(0) scale(1)' : 'translateX(100%) scale(0.95)',
        opacity: visible && !leaving ? 1 : 0,
        transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease',
        pointerEvents: 'auto',
      }}
    >
      {/* Icon */}
      <div style={{ flexShrink: 0, marginTop: '1px' }}>{TOAST_ICONS[toast.type]}</div>

      {/* Message */}
      <p style={{ flex: 1, fontSize: '13px', lineHeight: '1.5', color: '#EDF2F7', margin: 0 }}>
        {toast.message}
      </p>

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        style={{
          flexShrink: 0,
          width: '20px',
          height: '20px',
          borderRadius: '6px',
          border: 'none',
          background: 'rgba(255,255,255,0.06)',
          color: 'rgba(237,242,247,0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          marginTop: '1px',
          transition: 'background 0.15s, color 0.15s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.12)';
          (e.currentTarget as HTMLButtonElement).style.color = '#EDF2F7';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)';
          (e.currentTarget as HTMLButtonElement).style.color = 'rgba(237,242,247,0.4)';
        }}
        aria-label="Bildirimi kapat"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M2 2l6 6M8 2l-6 6"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration = 4000) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      {/* Toast container */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
